<template>
  <v-btn
    v-if="isFabVisible"
    variant="flat"
    color="primary"
    rounded="pill"
    :height="BEKKI_FAB_HEIGHT"
    :aria-label="label"
    class="bekki-fab position-fixed px-5"
    data-cc="bekki-fab"
    @click="openChat"
  >
    <span class="d-flex align-center ga-2">
      <v-icon
        icon="$star-sparkle"
        :size="ICON_SIZE"
        class="bekki-fab__sparkle"
      />

      {{ label }}
    </span>
  </v-btn>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import { useI18n } from 'vue-i18n';

import {
  BEKKI_FAB_HEIGHT,
  BEKKI_FAB_INSET
} from '@/components/bekki/constants';

import { useBekkiAccess } from '@/composables/useBekkiAccess';
import useBekkiChatStore from '@/stores/bekkiChat';

const { t } = useI18n();

const { isBekkiEnabled } = useBekkiAccess();
const bekkiChatStore = useBekkiChatStore();

const ICON_SIZE = 15;
const fabInset = `${BEKKI_FAB_INSET}px`;

const label = computed((): string => t('widgets.bekki.askBekki'));

const isFabVisible = computed(
  (): boolean => isBekkiEnabled.value && !bekkiChatStore.isOpen
);

const openChat = (): void => {
  bekkiChatStore.open();
};
</script>

<style scoped lang="scss">
.bekki-fab {
  --v-hover-opacity: 0;

  inset-block-end: v-bind(fabInset);
  inset-inline-end: v-bind(fabInset);

  /**
   * Above page content and the nav toggle (999), below both Vuetify's layout
   * band (app bar / navigation drawer sit at 1002+) and BMDrawerSheet (1100),
   * so the chat drawer, its scrim and the mobile nav always win.
   */
  z-index: 1000;

  /* Figma: linear-gradient(-72.05deg, #3BC6D7 9.47%, #1A31D5 104.98%). */
  background-image: linear-gradient(
    -72deg,
    rgb(var(--v-theme-ai-accent)) 9%,
    rgb(var(--v-theme-primary)) 105%
  );
  box-shadow: 0 4px 8px rgba(var(--v-theme-primary), 0.32);
  transition:
    transform 120ms ease,
    box-shadow 120ms ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 14px rgba(var(--v-theme-primary), 0.38);
  }

  &:focus-visible {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: var(--bm-spacing-half);
  }

  &:hover .bekki-fab__sparkle {
    animation: bekki-fab-spin 1400ms ease-out;
  }
}

.bekki-fab__sparkle {
  transform-origin: center;
}

@keyframes bekki-fab-spin {
  0% {
    transform: rotate(0deg);
  }
  50% {
    transform: rotate(180deg);
  }
  100% {
    transform: rotate(0deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .bekki-fab {
    transition: none;

    &:hover {
      transform: none;
    }

    &:hover .bekki-fab__sparkle {
      animation: none;
    }
  }
}
</style>
